import { Box, Stack, Typography, LinearProgress } from "@mui/material";
import { colors, shadows } from "../source/source.styles";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

const QualityBar = ({ label, count, percentage, color }) => {
  return (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: colors.textPrimary,
          minWidth: 90,
          flexShrink: 0,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1 }}>
        <LinearProgress
          variant="determinate"
          value={percentage}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: colors.bgPrimary,
            "& .MuiLinearProgress-bar": {
              bgcolor: color,
              borderRadius: 3,
            },
          }}
        />
      </Box>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 700,
          color: colors.textPrimary,
          minWidth: 50,
          textAlign: "right",
          flexShrink: 0,
        }}
      >
        {count}{" "}
        <span
          style={{ fontSize: 10, fontWeight: 500, color: colors.textMuted }}
        >
          ({percentage}%)
        </span>
      </Typography>
    </Stack>
  );
};

const DSPLeadQuality = ({ data }) => {
  const sources = data?.sources || [];

  // Aggregate by quality category based on qualityLabel from backend
  const qualityCounts = sources.reduce(
    (acc, source) => {
      const label = source.qualityLabel?.toLowerCase();
      if (label === "excellent") acc.excellent++;
      else if (label === "good") acc.good++;
      else if (label === "average") acc.average++;
      else acc.poor++;
      return acc;
    },
    { excellent: 0, good: 0, average: 0, poor: 0 },
  );

  // Aggregate leads by category (grade, pre-consult, post-consult)
  const categoryTotals = sources.reduce(
    (acc, source) => {
      // Grade leads (new, cold, warm, hot)
      acc.grade +=
        (source.newLeads || 0) +
        (source.hotLeads || 0) +
        (source.warmLeads || 0) +
        (source.coldLeads || 0);
      // Pre-consult leads (qualified, need_analysis, booked)
      acc.preConsult +=
        (source.qualifiedLeads || 0) +
        (source.needAnalysisLeads || 0) +
        (source.bookedLeads || 0);
      // Post-consult leads (post_consult, proposal_shared, negotiation, treatment_booked, won)
      acc.postConsult +=
        (source.postConsultLeads || 0) +
        (source.proposalSharedLeads || 0) +
        (source.negotiationLeads || 0) +
        (source.treatmentBookedLeads || 0) +
        (source.wonLeads || 0);
      // Dropped leads (shown separately)
      acc.dropped += source.droppedLeads || 0;
      return acc;
    },
    { grade: 0, preConsult: 0, postConsult: 0, dropped: 0 },
  );

  // Use total from summary for accurate count
  const totalLeads = data?.summary?.totalLeads || 0;

  // Calculate others (unknown statuses, etc.)
  const others =
    totalLeads -
    (categoryTotals.grade +
      categoryTotals.preConsult +
      categoryTotals.postConsult +
      categoryTotals.dropped);

  const gradePercent =
    totalLeads > 0 ? Math.round((categoryTotals.grade / totalLeads) * 100) : 0;
  const preConsultPercent =
    totalLeads > 0
      ? Math.round((categoryTotals.preConsult / totalLeads) * 100)
      : 0;
  const postConsultPercent =
    totalLeads > 0
      ? Math.round((categoryTotals.postConsult / totalLeads) * 100)
      : 0;
  const droppedPercent =
    totalLeads > 0
      ? Math.round((categoryTotals.dropped / totalLeads) * 100)
      : 0;
  const othersPercent =
    totalLeads > 0 ? Math.round((others / totalLeads) * 100) : 0;

  // Calculate arc paths for donut chart - use path elements for seamless rendering
  const radius = 70;
  const centerX = 90;
  const centerY = 90;
  const strokeWidth = 20;
  const outerRadius = radius + strokeWidth / 2;
  const innerRadius = radius - strokeWidth / 2;

  // Helper function to create SVG path arc
  const createArcPath = (startAngle, endAngle) => {
    const startRad = ((startAngle - 90) * Math.PI) / 180;
    const endRad = ((endAngle - 90) * Math.PI) / 180;

    const x1 = centerX + outerRadius * Math.cos(startRad);
    const y1 = centerY + outerRadius * Math.sin(startRad);
    const x2 = centerX + outerRadius * Math.cos(endRad);
    const y2 = centerY + outerRadius * Math.sin(endRad);

    const x3 = centerX + innerRadius * Math.cos(endRad);
    const y3 = centerY + innerRadius * Math.sin(endRad);
    const x4 = centerX + innerRadius * Math.cos(startRad);
    const y4 = centerY + innerRadius * Math.sin(startRad);

    const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

    return `M ${x1} ${y1} A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${x2} ${y2} L ${x3} ${y3} A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x4} ${y4} Z`;
  };

  const gradeAngle = (gradePercent / 100) * 360;
  const preConsultAngle = (preConsultPercent / 100) * 360;
  const postConsultAngle = (postConsultPercent / 100) * 360;
  const droppedAngle = (droppedPercent / 100) * 360;
  const othersAngle = (othersPercent / 100) * 360;

  // Cumulative angles for path calculation
  const startGrade = 0;
  const endGrade = gradeAngle;
  const startPre = gradeAngle;
  const endPre = gradeAngle + preConsultAngle;
  const startPost = gradeAngle + preConsultAngle;
  const endPost = gradeAngle + preConsultAngle + postConsultAngle;
  const startDropped = gradeAngle + preConsultAngle + postConsultAngle;
  const endDropped =
    gradeAngle + preConsultAngle + postConsultAngle + droppedAngle;
  const startOthers =
    gradeAngle + preConsultAngle + postConsultAngle + droppedAngle;
  const endOthers = 360;

  // Define colors for each category
  const categories = [
    {
      label: "Grade",
      count: categoryTotals.grade,
      percentage: gradePercent,
      color: "#3B82F6",
    },
    {
      label: "Pre-Consult",
      count: categoryTotals.preConsult,
      percentage: preConsultPercent,
      color: "#F59E0B",
    },
    {
      label: "Post-Consult",
      count: categoryTotals.postConsult,
      percentage: postConsultPercent,
      color: "#10B981",
    },
    {
      label: "Dropped",
      count: categoryTotals.dropped,
      percentage: droppedPercent,
      color: "#EF4444",
    },
    {
      label: "Others",
      count: others,
      percentage: othersPercent,
      color: "#9CA3AF",
    },
  ].filter((cat) => cat.count > 0);

  return (
    <Box
      sx={{
        bgcolor: "#FFFFFF",
        borderRadius: 3,
        p: 3,
        border: `1px solid ${colors.borderLight}`,
        boxShadow: shadows.card,
        height: "100%",
        width: "100%",
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.5,
            color: colors.textMuted,
            textTransform: "uppercase",
          }}
        >
          Lead Quality Analysis
        </Typography>
        <HelpOutlineIcon sx={{ fontSize: 16, color: colors.textMuted }} />
      </Stack>

      {/* Donut Chart - Compact */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ position: "relative", width: 180, height: 180 }}>
          <svg width="180" height="180" viewBox="0 0 180 180">
            <defs>
              <linearGradient
                id="grade-gradient-sq"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop offset="0%" stopColor="#3B82F6" />
                <stop offset="100%" stopColor="#60A5FA" />
              </linearGradient>
              <linearGradient
                id="preconsult-gradient-sq"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop offset="0%" stopColor="#F59E0B" />
                <stop offset="100%" stopColor="#FBBF24" />
              </linearGradient>
              <linearGradient
                id="postconsult-gradient-sq"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop offset="0%" stopColor="#10B981" />
                <stop offset="100%" stopColor="#34D399" />
              </linearGradient>
              <linearGradient
                id="dropped-gradient-sq"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop offset="0%" stopColor="#EF4444" />
                <stop offset="100%" stopColor="#F87171" />
              </linearGradient>
              <linearGradient
                id="others-gradient-sq"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop offset="0%" stopColor="#9CA3AF" />
                <stop offset="100%" stopColor="#D1D5DB" />
              </linearGradient>
            </defs>

            {/* Background circle */}
            <circle
              cx={centerX}
              cy={centerY}
              r={radius}
              fill="none"
              stroke={colors.bgPrimary}
              strokeWidth={strokeWidth}
            />

            {/* Grade segment */}
            {gradePercent > 0 && (
              <path
                d={createArcPath(startGrade, endGrade)}
                fill="url(#grade-gradient-sq)"
              />
            )}

            {/* Pre-Consult segment */}
            {preConsultPercent > 0 && (
              <path
                d={createArcPath(startPre, endPre)}
                fill="url(#preconsult-gradient-sq)"
              />
            )}

            {/* Post-Consult segment */}
            {postConsultPercent > 0 && (
              <path
                d={createArcPath(startPost, endPost)}
                fill="url(#postconsult-gradient-sq)"
              />
            )}

            {/* Dropped segment */}
            {droppedPercent > 0 && (
              <path
                d={createArcPath(startDropped, endDropped)}
                fill="url(#dropped-gradient-sq)"
              />
            )}

            {/* Others segment */}
            {othersPercent > 0 && (
              <path
                d={createArcPath(startOthers, endOthers)}
                fill="url(#others-gradient-sq)"
              />
            )}

            {/* Center text */}
            <text
              x={centerX}
              y={centerY - 5}
              textAnchor="middle"
              fill={colors.textPrimary}
              fontSize="28"
              fontWeight="700"
            >
              {totalLeads}
            </text>
            <text
              x={centerX}
              y={centerY + 15}
              textAnchor="middle"
              fill={colors.textSecondary}
              fontSize="10"
              fontWeight="600"
              letterSpacing="0.5"
            >
              LEADS
            </text>
          </svg>
        </Box>
      </Box>

      {/* Quality Bars */}
      {categories.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 4, color: colors.textMuted }}>
          <Typography sx={{ fontSize: 13 }}>No data available</Typography>
        </Box>
      ) : (
        <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
          {categories.map((category) => (
            <QualityBar
              key={category.label}
              label={category.label}
              count={category.count}
              percentage={category.percentage}
              color={category.color}
            />
          ))}
        </Box>
      )}

      {/* Total Summary */}
      <Box
        sx={{
          mt: 2,
          pt: 2,
          borderTop: `1px solid ${colors.borderLight}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          sx={{ fontSize: 11, fontWeight: 600, color: colors.textSecondary }}
        >
          Total Leads
        </Typography>
        <Typography
          sx={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary }}
        >
          {totalLeads}
        </Typography>
      </Box>
    </Box>
  );
};

export default DSPLeadQuality;
